import React from 'react';
import { Check } from 'lucide-react';
import { ScoreRing } from '../ui/ScoreRing';
import { EvidenceChip, EvidenceState } from '../ui/EvidenceChip';
import { barFillClass, barTextClass } from '../../lib/fit';
import { Reveal, useReveal } from './Reveal';
import { BwLogoGlyph } from './icons';

const POINTS = [
  'Fit and urgency are separate dimensions — green/amber/neutral for fit, red only for the clock.',
  'The verdict comes first; the factor breakdown is a drill-down, not the headline.',
  'An honesty line names exactly what the score was built on.',
];

interface Factor {
  label: string;
  weight: number;
  pct: number;
  evidence: { state: EvidenceState; text: string };
}

const FACTORS: Factor[] = [
  { label: 'NAICS Code Match', weight: 27, pct: 100, evidence: { state: 'grounded', text: 'Profile NAICS 541519' } },
  { label: 'Set-Aside Eligibility', weight: 22, pct: 100, evidence: { state: 'grounded', text: 'SDVOSB status verified' } },
  { label: 'Past Performance Fit', weight: 18, pct: 45, evidence: { state: 'inferred', text: 'Inferred from adjacent agency work' } },
];

/** Section 03 — verdict-first, two-tier scoring, with a live showcase card. */
export function ScoringSection() {
  const { ref, visible } = useReveal<HTMLDivElement>();

  return (
    <section id="scoring" className="bg-bw-surface border-y border-bw-border scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-24 flex flex-wrap gap-12 lg:gap-16 items-center">
        {/* Copy */}
        <Reveal className="flex-1 basis-[380px] min-w-[300px]">
          <div className="font-mono text-[11px] font-semibold tracking-[0.14em] text-bw-accent mb-4">
            03 — TWO-TIER SCORING
          </div>
          <h2 className="text-[clamp(30px,3.6vw,46px)] leading-[1.08] font-extrabold tracking-[-0.03em] text-bw-sidebar mb-[18px]">
            Spend AI where it actually matters.
          </h2>
          <p className="text-lg leading-[1.6] text-fit-weak mb-[26px]">
            Every opportunity gets a fast, free <strong className="text-bw-ink">baseline score</strong>{' '}
            the moment it lands. Run the deeper, token-heavy{' '}
            <strong className="text-bw-ink">AI deep-score</strong> only on the bids you care about —
            once, cached, and badged AI-verified.
          </p>
          <div className="flex flex-col gap-3.5">
            {POINTS.map((p) => (
              <div key={p} className="flex items-start gap-3.5">
                <span className="w-[22px] h-[22px] rounded-full bg-success-bg text-success flex items-center justify-center flex-none mt-0.5">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span className="text-[15px] leading-normal text-fit-weak">{p}</span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Verdict showcase card */}
        <div ref={ref} className="flex-1 basis-[400px] min-w-[300px]">
          <div className="bg-bw-surface border border-bw-border rounded-[14px] overflow-hidden shadow-panel">
            {/* panel header */}
            <div className="px-3.5 py-3 border-b border-bw-border">
              <div className="flex justify-between items-center mb-[7px]">
                <span className="text-[10px] font-bold text-bw-muted tracking-[0.1em] uppercase px-1.5 py-0.5 bg-bw-surface-subtle rounded">
                  OPEN
                </span>
                <span className="font-mono text-[11px] text-bw-muted">N3-22-Q-0148</span>
              </div>
              <div className="text-sm font-bold text-bw-ink leading-snug mb-1.5">
                IT Modernization &amp; Systems Support
              </div>
              <div className="flex items-center gap-2 text-[11px] text-bw-muted">
                Dept. of the Navy
                <span className="font-mono px-1.5 py-0.5 bg-bw-surface-subtle rounded">NAICS 541519</span>
              </div>
            </div>

            <div className="p-3.5">
              {/* verdict block */}
              <div className="bg-bw-surface-subtle border border-bw-border rounded-lg p-3.5 mb-2.5">
                <div className="flex items-start gap-3.5 mb-2.5">
                  <ScoreRing score={78} size={72} showScale aria-label="BidWin Score 78 out of 100" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span className="text-[13.5px] font-bold text-bw-ink leading-tight">
                        Strong fit — recommend pursuing
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-bw-primary-tint text-bw-primary border border-bw-primary rounded-full text-[10px] font-bold">
                        <CheckBadge /> AI-verified
                      </span>
                    </div>
                    <div className="text-[11.5px] text-bw-muted leading-relaxed">
                      <div className="mb-0.5">
                        <span className="text-fit-strong font-bold">↑ </span>Exact NAICS match (541519)
                      </div>
                      <div>
                        <span className="text-bw-accent font-bold">↓ </span>No Navy past performance on file
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-bw-muted px-2.5 py-2 bg-bw-surface rounded-md border border-dashed border-bw-border leading-normal mb-2.5">
                  Scored on NAICS code, set-aside type, and timeline. Run AI deep-score for full
                  capabilities analysis.
                </div>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-transparent border-[1.5px] border-bw-primary rounded-md text-bw-primary text-[11.5px] font-semibold"
                >
                  <BwLogoGlyph className="w-3.5 h-3.5" /> Run AI deep-score for capabilities fit
                </button>
              </div>

              {/* factor breakdown */}
              <div className="border border-bw-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-bw-surface">
                  <span className="text-[12.5px] font-semibold text-bw-ink">▾ Why this score</span>
                  <span className="font-mono text-xs font-medium text-fit-strong">78</span>
                </div>
                <div className="p-3 bg-bw-surface-subtle border-t border-bw-border">
                  {FACTORS.map((f, i) => (
                    <div key={f.label} className={i < FACTORS.length - 1 ? 'mb-3' : ''}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[11.5px] font-medium text-bw-ink">{f.label}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-[10px] text-bw-muted">{f.weight}% wt</span>
                          <span className={`font-mono text-[11.5px] font-medium ${barTextClass(f.pct)}`}>
                            {f.pct}%
                          </span>
                        </span>
                      </div>
                      <div className="w-full h-[3px] bg-bw-border rounded-full overflow-hidden mb-1.5">
                        <div
                          className={`h-full ${barFillClass(f.pct)} rounded-full`}
                          style={{
                            width: visible ? `${f.pct}%` : '0%',
                            transition: 'width 1s cubic-bezier(.2,.8,.2,1)',
                            transitionDelay: `${i * 0.12}s`,
                          }}
                        />
                      </div>
                      <EvidenceChip state={f.evidence.state}>{f.evidence.text}</EvidenceChip>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Small circled-check used by the AI-verified pill (inherits currentColor). */
function CheckBadge() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" />
      <path d="m8.4 12 2.4 2.4 4.8-5.2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
